import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Car, BadgeEuro, MessageCircle, Wallet, ShieldAlert, Plus, Trash2, Edit3, X, Check, Cloud, ExternalLink, Bookmark, FlaskConical, Camera } from "lucide-react";
import api, { resolveImageUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { LOGO_URL, formatEUR, formatKM } from "../lib/contact";
import { toast } from "sonner";
import PhotoStudio from "../components/PhotoStudio";

const PR_STATUSES = ["Neu", "In Prüfung", "Angebot gesendet", "Besichtigung vereinbart", "Abgeschlossen", "Abgelehnt"];
const VEHICLE_STATUSES = ["active", "reserved", "sold", "unpublished"];
const RESERVATION_STATUSES = ["Neu", "Bezahlt", "Besichtigung geplant", "Abgeschlossen", "Verfallen", "Rückerstattet"];

const StatCard = ({ icon: Icon, label, value, accent = false, testId }) => (
  <div className="bg-[#2E2E3C] border border-silver-soft rounded-2xl p-5" data-testid={testId}>
    <div className="flex items-start justify-between">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${accent ? "bg-[var(--na-red)] text-white" : "bg-white/5 text-zinc-300"}`}>
        <Icon className="w-4 h-4" />
      </div>
    </div>
    <p className="font-display text-3xl font-bold text-white mt-4">{value ?? "—"}</p>
    <p className="text-[11px] uppercase tracking-widest text-zinc-500 mt-1">{label}</p>
  </div>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = React.useState(null);
  const [vehicles, setVehicles] = React.useState([]);
  const [purchaseRequests, setPurchaseRequests] = React.useState([]);
  const [leads, setLeads] = React.useState({ test_drives: [], financing: [], contact: [] });
  const [reservations, setReservations] = React.useState([]);

  const [editingVehicle, setEditingVehicle] = React.useState(null);
  const [newOpen, setNewOpen] = React.useState(false);
  const [studioVehicleId, setStudioVehicleId] = React.useState("");
  const [activeTab, setActiveTab] = React.useState("vehicles");

  // Scroll to the top of the page whenever the admin switches tabs.
  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [activeTab]);

  const ensureAuth = React.useCallback(() => {
    const token = localStorage.getItem("na_admin_token");
    if (!token) navigate("/admin/login");
  }, [navigate]);

  const fetchAll = React.useCallback(async () => {
    try {
      const [s, v, pr, l, r] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/vehicles", { params: { status: "" } }),
        api.get("/admin/purchase-requests"),
        api.get("/admin/leads"),
        api.get("/admin/reservations"),
      ]);
      setStats(s.data);
      setVehicles(v.data);
      setPurchaseRequests(pr.data);
      setLeads(l.data);
      setReservations(r.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("na_admin_token");
        navigate("/admin/login");
      }
    }
  }, [navigate]);

  React.useEffect(() => {
    ensureAuth();
    fetchAll();
  }, [ensureAuth, fetchAll]);

  const logout = () => {
    localStorage.removeItem("na_admin_token");
    navigate("/admin/login");
  };

  const updateVehicleStatus = async (id, status) => {
    await api.patch(`/admin/vehicles/${id}`, { status });
    toast.success(`Status aktualisiert: ${status}`);
    fetchAll();
  };

  const deleteVehicle = async (id) => {
    if (!window.confirm("Fahrzeug wirklich löschen?")) return;
    await api.delete(`/admin/vehicles/${id}`);
    toast.success("Fahrzeug gelöscht");
    fetchAll();
  };

  const updatePR = async (id, patch) => {
    await api.patch(`/admin/purchase-requests/${id}`, patch);
    toast.success("Anfrage aktualisiert");
    fetchAll();
  };

  const updateReservation = async (id, patch) => {
    await api.patch(`/admin/reservations/${id}`, patch);
    toast.success("Reservierung aktualisiert");
    fetchAll();
  };

  return (
    <div className="min-h-screen bg-[var(--na-bg)]" data-testid="admin-dashboard-page">
      {/* Top bar */}
      <header className="border-b border-white/5 bg-[#1A1A22] sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-4 group">
            <img src={LOGO_URL} alt="NA Automobile" className="h-10 w-auto select-none transition-transform duration-300 group-hover:scale-[1.04]" draggable={false} />
            <span className="hidden md:flex flex-col border-l border-white/10 pl-4">
              <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Internal</span>
              <span className="font-display text-sm font-semibold text-white">Admin Dashboard</span>
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white inline-flex items-center gap-1.5">
              <ExternalLink className="w-3.5 h-3.5" /> Website
            </Link>
            <button onClick={logout} data-testid="admin-logout" className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white inline-flex items-center gap-1.5">
              <LogOut className="w-3.5 h-3.5" /> Abmelden
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12 space-y-10">
        <div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-white tracking-tight">Übersicht</h1>
          <p className="text-zinc-400 mt-1 text-sm">Inventar, Ankaufsanfragen und Leads auf einen Blick.</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <StatCard testId="stat-vehicles-total" icon={Car} label="Fahrzeuge gesamt" value={stats?.vehicles_total} />
          <StatCard testId="stat-vehicles-active" icon={Car} label="Aktiv" value={stats?.vehicles_active} accent />
          <StatCard testId="stat-vehicles-reserved" icon={Car} label="Reserviert" value={stats?.vehicles_reserved} />
          <StatCard testId="stat-vehicles-sold" icon={Car} label="Verkauft" value={stats?.vehicles_sold} />
          <StatCard testId="stat-pr-new" icon={BadgeEuro} label="Neue Ankaufsanfragen" value={stats?.purchase_requests_new} accent />
          <StatCard testId="stat-pr-damaged" icon={ShieldAlert} label="Beschädigte Fahrzeuge" value={stats?.purchase_requests_damaged} />
          <StatCard testId="stat-reservations" icon={Bookmark} label="Reservierungen (Neu)" value={stats?.reservations_new} accent />
          <StatCard testId="stat-test-drives" icon={Car} label="Probefahrt-Anfragen" value={stats?.test_drive_requests} />
          <StatCard testId="stat-financing" icon={Wallet} label="Finanzierungs-Leads" value={stats?.financing_requests} />
          <StatCard testId="stat-messages" icon={MessageCircle} label="Kontakt-Nachrichten" value={stats?.contact_messages} />
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-[#2E2E3C] border border-silver-soft rounded-xl p-1 h-auto flex flex-wrap gap-1">
            <TabsTrigger value="vehicles" data-testid="admin-tab-vehicles" className="px-4 py-2 rounded-xl text-zinc-400 data-[state=active]:bg-[var(--na-red)] data-[state=active]:text-white">Fahrzeuge</TabsTrigger>
            <TabsTrigger value="studio" data-testid="admin-tab-studio" className="px-4 py-2 rounded-xl text-zinc-400 data-[state=active]:bg-[var(--na-red)] data-[state=active]:text-white"><Camera className="w-3.5 h-3.5 mr-1.5 inline" />Photo Studio</TabsTrigger>
            <TabsTrigger value="purchase" data-testid="admin-tab-purchase" className="px-4 py-2 rounded-xl text-zinc-400 data-[state=active]:bg-[var(--na-red)] data-[state=active]:text-white">Ankaufsanfragen</TabsTrigger>
            <TabsTrigger value="reservations" data-testid="admin-tab-reservations" className="px-4 py-2 rounded-xl text-zinc-400 data-[state=active]:bg-[var(--na-red)] data-[state=active]:text-white">Reservierungen</TabsTrigger>
            <TabsTrigger value="leads" data-testid="admin-tab-leads" className="px-4 py-2 rounded-xl text-zinc-400 data-[state=active]:bg-[var(--na-red)] data-[state=active]:text-white">Leads</TabsTrigger>
            <TabsTrigger value="connectors" data-testid="admin-tab-connectors" className="px-4 py-2 rounded-xl text-zinc-400 data-[state=active]:bg-[var(--na-red)] data-[state=active]:text-white">Sync / Connectors</TabsTrigger>
          </TabsList>

          {/* Vehicles tab */}
          <TabsContent value="vehicles" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-semibold text-white">Inventar</h2>
              <Button onClick={() => setNewOpen(true)} data-testid="admin-new-vehicle" className="bg-[var(--na-red)] hover:bg-[var(--na-red-hover)] text-white rounded-xl">
                <Plus className="w-4 h-4 mr-1" /> Fahrzeug
              </Button>
            </div>
            <div className="overflow-x-auto rounded-2xl border border-white/5 bg-[#232330]">
              <table className="w-full min-w-[920px] text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/5">
                    <th className="text-left p-4">Fahrzeug</th>
                    <th className="text-left p-4">Baujahr</th>
                    <th className="text-left p-4">KM</th>
                    <th className="text-left p-4">Preis</th>
                    <th className="text-left p-4">Status</th>
                    <th className="text-right p-4">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {vehicles.map((v) => (
                    <tr key={v.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`admin-vehicle-row-${v.id}`}>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          {v.images?.[0] && <img src={resolveImageUrl(v.images[0])} alt="" className="w-16 h-10 rounded-xl object-cover" />}
                          <div>
                            <p className="text-white font-medium">{v.make} {v.model}</p>
                            <p className="text-[10px] uppercase tracking-widest text-zinc-500">{v.fuel} · {v.transmission}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-zinc-300">{v.year}</td>
                      <td className="p-4 text-zinc-300">{formatKM(v.mileage)}</td>
                      <td className="p-4 text-white font-medium">{formatEUR(v.price)}</td>
                      <td className="p-4">
                        <Select value={v.status} onValueChange={(s) => updateVehicleStatus(v.id, s)}>
                          <SelectTrigger data-testid={`admin-vehicle-status-${v.id}`} className="bg-[#1A1A22] border-white/10 text-white w-40 h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#232330] border-white/10 text-white">
                            {VEHICLE_STATUSES.map((s) => (
                              <SelectItem key={s} value={s} className="focus:bg-white/10 focus:text-white">{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => { setStudioVehicleId(v.id); setActiveTab("studio"); }}
                            data-testid={`admin-vehicle-studio-${v.id}`}
                            title="Im Photo Studio bearbeiten"
                            className="p-1.5 rounded-xl hover:bg-white/10 text-zinc-300"
                          >
                            <Camera className="w-4 h-4" />
                          </button>
                          <button onClick={() => setEditingVehicle(v)} data-testid={`admin-vehicle-edit-${v.id}`} className="p-1.5 rounded-xl hover:bg-white/10 text-zinc-300">
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button onClick={() => deleteVehicle(v.id)} data-testid={`admin-vehicle-delete-${v.id}`} className="p-1.5 rounded-xl hover:bg-white/10 text-[var(--na-red)]">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {vehicles.length === 0 && (
                    <tr><td colSpan={6} className="p-12 text-center text-zinc-500">Keine Fahrzeuge angelegt.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Photo Studio tab */}
          <TabsContent value="studio" className="space-y-4">
            <PhotoStudio
              vehicles={vehicles}
              defaultVehicleId={studioVehicleId}
              onRefreshVehicles={fetchAll}
            />
          </TabsContent>

          {/* Purchase requests tab */}
          <TabsContent value="purchase" className="space-y-4">
            <h2 className="font-display text-xl font-semibold text-white">Ankaufsanfragen</h2>
            <div className="space-y-3">
              {purchaseRequests.length === 0 && (
                <div className="p-12 text-center text-zinc-500 bg-[#232330] rounded-2xl border border-white/5">Keine Anfragen</div>
              )}
              {purchaseRequests.map((pr) => (
                <PRCard key={pr.id} pr={pr} onSave={updatePR} />
              ))}
            </div>
          </TabsContent>

          {/* Reservations tab */}
          <TabsContent value="reservations" className="space-y-4">
            <h2 className="font-display text-xl font-semibold text-white">Reservierungen <span className="text-zinc-500 text-sm font-body">(€200 Anzahlung)</span></h2>
            <div className="space-y-3">
              {reservations.length === 0 && (
                <div className="p-12 text-center text-zinc-500 bg-[#232330] rounded-2xl border border-white/5">Noch keine Reservierungen</div>
              )}
              {reservations.map((res) => (
                <ReservationCard key={res.id} res={res} onSave={updateReservation} />
              ))}
            </div>
          </TabsContent>

          {/* Leads tab */}
          <TabsContent value="leads" className="space-y-8">
            <LeadGroup title="Probefahrt-Anfragen" rows={leads.test_drives} cols={["vehicle_name", "name", "phone", "email", "preferred_date"]} testId="leads-testdrives" />
            <LeadGroup title="Finanzierungs-Anfragen" rows={leads.financing} cols={["vehicle_name", "name", "phone", "email", "vehicle_price", "monthly_rate"]} testId="leads-financing" />
            <LeadGroup title="Kontakt-Nachrichten" rows={leads.contact} cols={["name", "email", "phone", "subject", "message"]} testId="leads-contact" />
          </TabsContent>

          {/* Connectors tab */}
          <TabsContent value="connectors" className="space-y-6">
            <h2 className="font-display text-xl font-semibold text-white">Externe Plattformen</h2>
            <p className="text-zinc-400 text-sm max-w-2xl">
              Sobald aktiv geschaltet, synchronisiert NA Automobile Ihre Inserate automatisch mit den
              wichtigsten österreichischen Fahrzeugportalen. Echte API-Integrationen folgen in der nächsten Phase.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { name: "gebrauchtwagen.at", note: "Sync aller aktiven Inserate inkl. Foto-Upload." },
                { name: "AutoScout24", note: "Bidirektionaler Sync mit Lead-Rückübertragung." },
                { name: "willhaben", note: "Tägliche Synchronisierung mit Versionsabgleich." },
                { name: "CSV Import", note: "Manueller Import aus eigenen Tabellen." },
              ].map((c) => (
                <div key={c.name} className="bg-[#2E2E3C] border border-silver-soft rounded-2xl p-5 space-y-3" data-testid={`connector-${c.name.toLowerCase().replace(/\W/g, '-')}`}>
                  <div className="flex items-center justify-between">
                    <Cloud className="w-5 h-5 text-zinc-400" />
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500 border border-white/10 rounded-xl px-2 py-0.5">Coming soon</span>
                  </div>
                  <p className="font-display font-semibold text-white">{c.name}</p>
                  <p className="text-xs text-zinc-400 leading-relaxed">{c.note}</p>
                  <Button disabled className="w-full bg-white/5 text-zinc-500 cursor-not-allowed rounded-xl text-xs">Verbinden</Button>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {(newOpen || editingVehicle) && (
        <VehicleFormDialog
          vehicle={editingVehicle}
          onClose={() => { setEditingVehicle(null); setNewOpen(false); }}
          onSaved={() => { fetchAll(); setEditingVehicle(null); setNewOpen(false); }}
          onOpenInStudio={(vid) => {
            setEditingVehicle(null);
            setNewOpen(false);
            setStudioVehicleId(vid);
            setActiveTab("studio");
          }}
        />
      )}
    </div>
  );
};

const ReservationCard = ({ res, onSave }) => {
  const [status, setStatus] = React.useState(res.status);
  return (
    <div className="bg-[#2E2E3C] border border-silver-soft rounded-2xl p-5 lg:p-6" data-testid={`admin-reservation-card-${res.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <p className="text-[11px] uppercase tracking-widest text-zinc-500">{new Date(res.created_at).toLocaleString("de-AT")}</p>
            {res.payment_mode === "demo" && (
              <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-yellow-300/90 bg-yellow-500/10 border border-yellow-500/30 rounded-full px-2 py-0.5">
                <FlaskConical className="w-2.5 h-2.5" /> Demo-Zahlung
              </span>
            )}
          </div>
          <p className="font-display text-lg font-semibold text-white mt-1">{res.vehicle_name || res.vehicle_id}</p>
          <p className="text-sm text-zinc-400">Reserviert bis {res.expires_at ? new Date(res.expires_at).toLocaleDateString("de-AT") : "—"}</p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500">Anzahlung</p>
          <p className="font-display text-2xl font-bold text-[var(--na-red)] tracking-tight">{formatEUR(res.amount || 200)}</p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
        <div className="bg-[#1A1A22] rounded-xl p-3 border border-white/5">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Kunde</p>
          <p className="text-white">{res.name}</p>
          <p className="text-zinc-400 text-xs">{res.phone} · {res.email}</p>
        </div>
        <div className="bg-[#1A1A22] rounded-xl p-3 border border-white/5">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Wunschdatum</p>
          <p className="text-white">{res.preferred_date || "—"}</p>
        </div>
        <div className="bg-[#1A1A22] rounded-xl p-3 border border-white/5">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Fahrzeugpreis</p>
          <p className="text-white">{res.vehicle_price ? formatEUR(res.vehicle_price) : "—"}</p>
        </div>
      </div>

      {res.message && (
        <p className="mt-3 text-sm text-zinc-300 leading-relaxed bg-[#1A1A22] rounded-xl p-3 border border-white/5">{res.message}</p>
      )}

      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <Label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid={`admin-reservation-status-${res.id}`} className="bg-[#1A1A22] border-white/10 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#232330] border-white/10 text-white">
              {RESERVATION_STATUSES.map((s) => (<SelectItem key={s} value={s} className="focus:bg-white/10 focus:text-white">{s}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <Button
          onClick={() => onSave(res.id, { status })}
          data-testid={`admin-reservation-save-${res.id}`}
          className="bg-[var(--na-red)] hover:bg-[var(--na-red-hover)] text-white rounded-xl"
        >
          <Check className="w-4 h-4 mr-1" /> Speichern
        </Button>
      </div>
    </div>
  );
};

const PRCard = ({ pr, onSave }) => {
  const [status, setStatus] = React.useState(pr.status);
  const [offer, setOffer] = React.useState(pr.dealer_offer || "");
  return (
    <div className="bg-[#2E2E3C] border border-silver-soft rounded-2xl p-5 lg:p-6" data-testid={`admin-pr-card-${pr.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-widest text-zinc-500">{new Date(pr.created_at).toLocaleString("de-AT")}</p>
          <p className="font-display text-lg font-semibold text-white mt-1">{pr.make} {pr.model} · {pr.year}</p>
          <p className="text-sm text-zinc-400">{formatKM(pr.mileage)} · {pr.fuel} · {pr.transmission} · {pr.power_hp} PS</p>
        </div>
        <div className="text-right">
          {pr.ai_price_min && (
            <>
              <p className="text-[10px] uppercase tracking-widest text-zinc-500">KI-Spanne</p>
              <p className="font-display text-lg font-bold text-white">{formatEUR(pr.ai_price_min)} – {formatEUR(pr.ai_price_max)}</p>
            </>
          )}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
        <div className="bg-[#1A1A22] rounded-xl p-3 border border-white/5">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Zustand</p>
          <p className="text-white">{pr.condition || "—"} · {pr.roadworthy}</p>
        </div>
        <div className="bg-[#1A1A22] rounded-xl p-3 border border-white/5">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Kontakt</p>
          <p className="text-white">{pr.name || "—"}</p>
          <p className="text-zinc-400">{pr.phone} · {pr.email}</p>
        </div>
        <div className="bg-[#1A1A22] rounded-xl p-3 border border-white/5">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Merkmale</p>
          <p className="text-zinc-300 text-xs">{(pr.flags || []).join(", ") || "—"}</p>
        </div>
      </div>

      {pr.description && (
        <p className="mt-3 text-sm text-zinc-300 leading-relaxed bg-[#1A1A22] rounded-xl p-3 border border-white/5">{pr.description}</p>
      )}
      {pr.photos?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {pr.photos.map((p, i) => (<img key={i} src={resolveImageUrl(p)} alt="" className="w-20 h-20 rounded-xl object-cover border border-white/10" />))}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[180px]">
          <Label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid={`admin-pr-status-${pr.id}`} className="bg-[#1A1A22] border-white/10 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#232330] border-white/10 text-white">
              {PR_STATUSES.map((s) => (<SelectItem key={s} value={s} className="focus:bg-white/10 focus:text-white">{s}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[180px]">
          <Label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">Händlerangebot (€)</Label>
          <Input data-testid={`admin-pr-offer-${pr.id}`} type="number" value={offer} onChange={(e) => setOffer(e.target.value)} className="bg-[#1A1A22] border-white/10 text-white" placeholder="z.B. 12500" />
        </div>
        <Button
          onClick={() => onSave(pr.id, { status, dealer_offer: offer ? Number(offer) : null })}
          data-testid={`admin-pr-save-${pr.id}`}
          className="bg-[var(--na-red)] hover:bg-[var(--na-red-hover)] text-white rounded-xl"
        >
          <Check className="w-4 h-4 mr-1" /> Speichern
        </Button>
      </div>
    </div>
  );
};

const LeadGroup = ({ title, rows, cols, testId }) => (
  <section data-testid={testId}>
    <h3 className="font-display text-lg font-semibold text-white mb-3">{title} <span className="text-zinc-500 text-sm font-body">({rows.length})</span></h3>
    {rows.length === 0 ? (
      <div className="p-8 text-center text-zinc-500 bg-[#232330] rounded-2xl border border-white/5 text-sm">Keine Einträge</div>
    ) : (
      <div className="overflow-x-auto rounded-2xl border border-white/5 bg-[#232330]">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/5">
              {cols.map((c) => (<th key={c} className="text-left p-3">{c.replace(/_/g, " ")}</th>))}
              <th className="text-left p-3">Datum</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-white/5 hover:bg-white/5">
                {cols.map((c) => (
                  <td key={c} className="p-3 text-zinc-300">{
                    c === "vehicle_price" || c === "monthly_rate"
                      ? (r[c] ? formatEUR(r[c]) : "—")
                      : (r[c] || "—")
                  }</td>
                ))}
                <td className="p-3 text-zinc-500">{new Date(r.created_at).toLocaleString("de-AT")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </section>
);

const VehicleFormDialog = ({ vehicle, onClose, onSaved, onOpenInStudio }) => {
  const isEdit = Boolean(vehicle?.id);
  const [form, setForm] = React.useState(
    vehicle || {
      make: "BMW",
      model: "",
      year: 2022,
      price: 25000,
      mileage: 50000,
      fuel: "Diesel",
      transmission: "Automatik",
      power_hp: 150,
      body_type: "Limousine",
      color: "",
      location: "2201 Hagenbrunn",
      description: "",
      equipment: [],
      images: [],
      badge: "",
      status: "active",
    }
  );
  const [uploading, setUploading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(res.data.url);
      }
      setForm((p) => ({ ...p, images: [...(p.images || []), ...uploaded] }));
      toast.success(`${uploaded.length} Foto(s) hochgeladen`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        year: Number(form.year),
        price: Number(form.price),
        mileage: Number(form.mileage),
        power_hp: Number(form.power_hp || 0),
        equipment: typeof form.equipment === "string"
          ? form.equipment.split(",").map((s) => s.trim()).filter(Boolean)
          : form.equipment,
      };
      if (isEdit) {
        await api.patch(`/admin/vehicles/${vehicle.id}`, payload);
        toast.success("Fahrzeug aktualisiert");
      } else {
        await api.post("/admin/vehicles", payload);
        toast.success("Fahrzeug angelegt");
      }
      onSaved();
    } catch (err) {
      toast.error("Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const equipmentString = Array.isArray(form.equipment) ? form.equipment.join(", ") : form.equipment;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#232330] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{isEdit ? "Fahrzeug bearbeiten" : "Neues Fahrzeug"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <D label="Marke"><Input data-testid="vehicle-form-make" value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} className="bg-[#1A1A22] border-white/10" /></D>
            <D label="Modell"><Input data-testid="vehicle-form-model" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="bg-[#1A1A22] border-white/10" /></D>
            <D label="Baujahr"><Input data-testid="vehicle-form-year" type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className="bg-[#1A1A22] border-white/10" /></D>
            <D label="Preis (€)"><Input data-testid="vehicle-form-price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="bg-[#1A1A22] border-white/10" /></D>
            <D label="Kilometerstand"><Input data-testid="vehicle-form-mileage" type="number" value={form.mileage} onChange={(e) => setForm({ ...form, mileage: e.target.value })} className="bg-[#1A1A22] border-white/10" /></D>
            <D label="Leistung (PS)"><Input data-testid="vehicle-form-power" type="number" value={form.power_hp} onChange={(e) => setForm({ ...form, power_hp: e.target.value })} className="bg-[#1A1A22] border-white/10" /></D>
            <D label="Kraftstoff"><Input data-testid="vehicle-form-fuel" value={form.fuel} onChange={(e) => setForm({ ...form, fuel: e.target.value })} className="bg-[#1A1A22] border-white/10" /></D>
            <D label="Getriebe"><Input data-testid="vehicle-form-transmission" value={form.transmission} onChange={(e) => setForm({ ...form, transmission: e.target.value })} className="bg-[#1A1A22] border-white/10" /></D>
            <D label="Farbe"><Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="bg-[#1A1A22] border-white/10" /></D>
            <D label="Standort"><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="bg-[#1A1A22] border-white/10" /></D>
            <D label="Badge (optional)"><Input value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} className="bg-[#1A1A22] border-white/10" placeholder="z.B. Top Zustand" /></D>
            <D label="Status">
              <Select value={form.status} onValueChange={(s) => setForm({ ...form, status: s })}>
                <SelectTrigger className="bg-[#1A1A22] border-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#232330] border-white/10 text-white">
                  {VEHICLE_STATUSES.map((s) => (<SelectItem key={s} value={s} className="focus:bg-white/10 focus:text-white">{s}</SelectItem>))}
                </SelectContent>
              </Select>
            </D>
          </div>
          <D label="Beschreibung">
            <textarea
              data-testid="vehicle-form-description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              className="w-full bg-[#1A1A22] border border-white/10 rounded-xl px-3 py-2 text-sm text-white"
            />
          </D>
          <D label="Ausstattung (Komma-getrennt)">
            <textarea
              data-testid="vehicle-form-equipment"
              value={equipmentString}
              onChange={(e) => setForm({ ...form, equipment: e.target.value })}
              rows={2}
              className="w-full bg-[#1A1A22] border border-white/10 rounded-xl px-3 py-2 text-sm text-white"
            />
          </D>
          <D label="Bilder">
            <div className="flex flex-wrap gap-2">
              {(form.images || []).map((img, i) => (
                <div key={i} className="relative w-20 h-20 rounded-xl overflow-hidden border border-white/10">
                  <img src={resolveImageUrl(img)} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => setForm({ ...form, images: form.images.filter((_, j) => j !== i) })} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/80 text-white flex items-center justify-center">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <label className="w-20 h-20 rounded-xl border border-dashed border-white/20 bg-[#1A1A22] flex items-center justify-center text-zinc-400 hover:text-white cursor-pointer">
                {uploading ? "…" : <Plus className="w-5 h-5" />}
                <input type="file" accept="image/*,.heic,.heif" multiple onChange={upload} className="hidden" />
              </label>
            </div>
            {isEdit && onOpenInStudio && (
              <button
                type="button"
                onClick={() => onOpenInStudio(vehicle.id)}
                data-testid="vehicle-form-open-studio"
                className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-white/15 hover:bg-white/5 text-sm text-white transition-colors"
              >
                <Camera className="w-4 h-4 text-[var(--na-red)]" /> Im Photo Studio bearbeiten
              </button>
            )}
          </D>
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t border-white/10">
          <Button variant="outline" onClick={onClose} className="border-white/15 text-white bg-transparent hover:bg-white/5">Abbrechen</Button>
          <Button onClick={save} disabled={saving} data-testid="vehicle-form-save" className="bg-[var(--na-red)] hover:bg-[var(--na-red-hover)] text-white rounded-xl">{saving ? "Speichern…" : "Speichern"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const D = ({ label, children }) => (
  <div>
    <Label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">{label}</Label>
    {children}
  </div>
);

export default AdminDashboard;
